import React, { useState, useRef, useEffect } from 'react';
import { X, Upload, Camera, Star, Package, Leaf, Droplets, Zap, Award, Search, ToggleLeft, ToggleRight } from 'lucide-react';
import API_URL from '../config/ApiUrl';
import { useUser } from '../context/UserContext';
export const ProductSubmissionModal = ({ isOpen, onClose, onSubmit,  }) => {
  const [mode, setMode] = useState('submit'); // 'submit' or 'review'
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [formData, setFormData] = useState({
    brand: '',
    product_name: '',
    type: '',
    rating: 0,
    review: '',
    attributes: {
      THC: '',
      CBD: '',
      strain_type: '',
      terpenes: [],
      flavor: '',
      size: '',
      category: ''
    }
  });

    const { 
      user, 
      isAuthenticated, 
      logout, 
      userStats, 
      statsLoading 
    } = useUser();
  
  // Review mode specific state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [reviewData, setReviewData] = useState({
    rating: 0,
    content: ''
  });
  
  const [labelImage, setLabelImage] = useState(null);
  const [labelImagePreview, setLabelImagePreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const fileInputRef = useRef(null);

  const [isAnonymous, setIsAnonymous] = useState(false);

  // Handle modal animations
  useEffect(() => {
    if (isOpen) {
      setModalVisible(true);
    } else {
      const timer = setTimeout(() => setModalVisible(false), 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]);

  useEffect(() => {
  if (searchQuery.length >= 1) {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 3 || searchQuery.length === 1) {
        searchProducts(searchQuery);
      }
    }, 300);
    
    return () => clearTimeout(timer);
  } else {
    setSearchResults([]);
  }
}, [searchQuery]);

  const productTypes = [
    { value: 'flower', label: '🌿 Flower', icon: Leaf },
    { value: 'cart', label: '💨 Vape Cart', icon: Zap },
    { value: 'edible', label: '🍬 Edibles', icon: Package },
    { value: 'pre-roll', label: '🚬 Pre-roll', icon: Leaf },
    { value: 'concentrate', label: '💎 Concentrate', icon: Droplets },
    { value: 'other', label: '📦 Other', icon: Package }
  ];

  const strainTypes = ['Sativa', 'Indica', 'Hybrid', 'CBD'];
  const commonTerpenes = ['Myrcene', 'Limonene', 'Caryophyllene', 'Pinene', 'Linalool', 'Terpinolene', 'Humulene'];

  const searchProducts = async (query) => {
    setIsSearching(true);
    try {
      const response = await fetch(`${API_URL}/api/products/search?q=${encodeURIComponent(query)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to search products');
      }
      console.log('--------------RESPONSE LETS READ----------------');
      const results = await response.json();
      console.log(results);
      console.log('--------------RESPONSE LETS READ----------------');


      const formattedResults = results.map(product => ({
        id: product.id,
        brand: product.brand,
        product_name: product.name,
        type: product.type,
        rating: product.averageRating || 0,
        reviewCount: product.reviewCount || 0,
        attributes: product.attributes || []
      }));
      
      setSearchResults(formattedResults);
    } catch (error) {
      console.error('Error searching products:', error);
      setSearchResults([]);
      alert('Error searching products. Please try again.');
    } finally {
      setIsSearching(false);
    }
  };

  const handleModeToggle = () => {
    setIsTransitioning(true);
    
    // Wait for the slide-out animation to complete
    setTimeout(() => {
      const newMode = mode === 'submit' ? 'review' : 'submit';
      setMode(newMode);
      
      // Reset states when switching modes
      if (newMode === 'review') {
        setSearchQuery('');
        setSearchResults([]);
        setSelectedProduct(null);
        setReviewData({ rating: 0, content: '' });
      } else {
        setFormData({
          brand: '',
          product_name: '',
          type: '',
          rating: 0,
          review: '',
          attributes: {
            THC: '',
            CBD: '',
            strain_type: '',
            terpenes: [],
            flavor: '',
            size: '',
            category: ''
          }
        });
        setLabelImage(null);
        setLabelImagePreview(null);
      }
      
      // Wait a bit more for the new content to slide in
      setTimeout(() => {
        setIsTransitioning(false);
      }, 100);
    }, 250);
  };

  const handleImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setLabelImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLabelImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleAttributeChange = (key, value) => {
    setFormData(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        [key]: value
      }
    }));
  };

  const handleTerpeneToggle = (terpene) => {
    setFormData(prev => ({
      ...prev,
      attributes: {
        ...prev.attributes,
        terpenes: prev.attributes.terpenes.includes(terpene)
          ? prev.attributes.terpenes.filter(t => t !== terpene)
          : [...prev.attributes.terpenes, terpene]
      }
    }));
  };

  const convertImageToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async () => {
    if (!formData.brand || !formData.product_name) {
      alert('Please fill in required fields');
      return;
    }

    setIsSubmitting(true);

    try {
      let label_image_base64 = null;
      if (labelImage) {
        label_image_base64 = await convertImageToBase64(labelImage);
      }

      const submissionData = {
        ...formData,
        user_id: 1,
        label_image_base64,
        attributes: JSON.stringify(formData.attributes),
        is_anonymous: isAnonymous
      };

      await onSubmit(submissionData);
      
      // Reset form
      setFormData({
        brand: '',
        product_name: '',
        type: '',
        rating: 0,
        review: '',
        attributes: {
          THC: '',
          CBD: '',
          strain_type: '',
          terpenes: [],
          flavor: '',
          size: '',
          category: ''
        }
      });
      setLabelImage(null);
      setLabelImagePreview(null);
      
      onClose();
    } catch (error) {
      console.error('Error submitting product:', error);
      alert('Error submitting product. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitReview = async () => {
    if (!selectedProduct || !reviewData.rating || !reviewData.content) {
      alert('Please select a product, provide a rating, and write a review');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/reviews`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          product_id: selectedProduct.id,
          rating: reviewData.rating,
          content: reviewData.content,
          is_anonymous: isAnonymous
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit review');
      }

      setReviewData({ rating: 0, content: '' });
      setSelectedProduct(null);
      setSearchQuery('');
      setSearchResults([]);
      
      alert('Review submitted successfully!');
      onClose();
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Error submitting review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      searchProducts(searchQuery.trim());
    }
  };

  const handleProductSelect = (product) => {
    setSelectedProduct(product);
  };

  if (!modalVisible) return null;

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      backdropFilter: 'blur(8px)',
      opacity: isOpen ? 1 : 0,
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    }}>
      <div style={{
        backgroundColor: '#1a1a1a',
        borderRadius: '24px',
        border: '1px solid #333',
        width: '100%',
        maxWidth: '800px',
        maxHeight: '90vh',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px rgba(255, 255, 255, 0.05)',
        transform: isOpen ? 'scale(1) translateY(0px)' : 'scale(0.95) translateY(20px)',
        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
      }}>
        {/* Header with enhanced gradient */}
        <div style={{
          padding: '28px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 50%, #1a1a1a 100%)',
          position: 'relative',
          
        }}>
          {/* Animated background orbs */}
          <div style={{
            position: 'absolute',
            top: '-50%',
            left: '-20%',
            width: '200px',
            height: '200px',
            background: 'radial-gradient(circle, rgba(0, 212, 170, 0.15) 0%, transparent 70%)',
            borderRadius: '50%',
            animation: 'float 6s ease-in-out infinite',
            pointerEvents: 'none'
          }} />
          <div style={{
            position: 'absolute',
            bottom: '-50%',
            right: '-10%',
            width: '150px',
            height: '150px',
            background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
            borderRadius: '50%',
            animation: 'float 8s ease-in-out infinite reverse',
            pointerEvents: 'none'
          }} />
          
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '16px',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{
              width: '48px',
              height: '48px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 16px rgba(0, 212, 170, 0.3)',
              animation: 'glow 2s ease-in-out infinite alternate'
            }}>
              <Award size={24} style={{ color: 'white' }} />
            </div>
            <div>
              <h2 style={{
                margin: 0,
                fontSize: '28px',
                fontWeight: '700',
                color: '#e5e5e5',
                background: 'linear-gradient(135deg, #e5e5e5 0%, #00d4aa 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                {mode === 'submit' ? 'Submit Product & Review' : 'Review Existing Product'}
              </h2>
              <p style={{
                margin: '4px 0 0 0',
                fontSize: '14px',
                color: '#888',
                opacity: 0.8
              }}>
                {mode === 'submit' ? 'Add a new product with your review' : 'Review a product from our database'}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255, 255, 255, 0.05)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
              color: '#888',
              cursor: 'pointer',
              padding: '12px',
              borderRadius: '12px',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              backdropFilter: 'blur(10px)',
              position: 'relative',
              zIndex: 1
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.transform = 'scale(1.05) rotate(90deg)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.2)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
              e.currentTarget.style.color = '#888';
              e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
            }}
          >
            <X size={24} />
          </button>
        </div>

        {/* Enhanced Mode Toggle with Professional Animation */}
        <div style={{
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'linear-gradient(135deg, #1f1f1f 0%, #2a2a2a 100%)',
          position: 'relative',
          overflow: 'hidden'
        }}>
          {/* Animated background indicator */}
          <div style={{
            position: 'absolute',
            top: 0,
            left: mode === 'submit' ? '0%' : '50%',
            width: '50%',
            height: '100%',
            background: 'linear-gradient(135deg, rgba(0, 212, 170, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
            transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
            pointerEvents: 'none'
          }} />
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '24px',
            marginBottom: '5%',
            marginBottom: mode === 'submit' ? '10%' : '',
            position: 'relative',
            zIndex: 1
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              opacity: mode === 'submit' ? 1 : 0.6,
              transform: mode === 'submit' ? 'scale(1.05)' : 'scale(1)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: mode === 'submit' ? '#00d4aa' : 'transparent',
                transition: 'all 0.3s ease',
                boxShadow: mode === 'submit' ? '0 0 16px rgba(0, 212, 170, 0.8)' : 'none'
              }} />
              <span style={{
                color: mode === 'submit' ? '#00d4aa' : '#888',
                fontSize: '16px',
                fontWeight: '700',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                textShadow: mode === 'submit' ? '0 0 20px rgba(0, 212, 170, 0.5)' : 'none'
              }}>
                Submit New Product
              </span>
            </div>
            
            <button
              onClick={handleModeToggle}
              disabled={isTransitioning}
              style={{
                background: 'linear-gradient(135deg, #333 0%, #2a2a2a 50%, #1f1f1f 100%)',
                border: '2px solid rgba(0, 212, 170, 0.3)',
                cursor: 'pointer',
                padding: '12px',
                borderRadius: '20px',
                display: 'flex',
                alignItems: 'center',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                boxShadow: '0 8px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
                position: 'relative',
                overflow: 'hidden'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'scale(1.1)';
                e.currentTarget.style.borderColor = 'rgba(0, 212, 170, 0.6)';
                e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 212, 170, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'scale(1)';
                e.currentTarget.style.borderColor = 'rgba(0, 212, 170, 0.3)';
                e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.1)';
              }}
            >
              {/* Animated background shine */}
              <div style={{
                position: 'absolute',
                top: '-50%',
                left: '-50%',
                width: '200%',
                height: '200%',
                background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
                animation: 'shine 3s ease-in-out infinite',
                pointerEvents: 'none'
              }} />
              
              {mode === 'submit' ? (
                <ToggleLeft size={32} style={{ 
                  color: '#00d4aa',
                  transform: isTransitioning ? 'rotateY(180deg)' : 'rotateY(0deg)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  filter: 'drop-shadow(0 0 8px rgba(0, 212, 170, 0.6))'
                }} />
              ) : (
                <ToggleRight size={32} style={{ 
                  color: '#8b5cf6',
                  transform: isTransitioning ? 'rotateY(180deg)' : 'rotateY(0deg)',
                  transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                  filter: 'drop-shadow(0 0 8px rgba(139, 92, 246, 0.6))'
                }} />
              )}
            </button>
            
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              opacity: mode === 'review' ? 1 : 0.6,
              transform: mode === 'review' ? 'scale(1.05)' : 'scale(1)',
              transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
            }}>
              <span style={{
                color: mode === 'review' ? '#8b5cf6' : '#888',
                fontSize: '16px',
                fontWeight: '700',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                textShadow: mode === 'review' ? '0 0 20px rgba(139, 92, 246, 0.5)' : 'none'
              }}>
                Review Existing Product
              </span>
              <div style={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                backgroundColor: mode === 'review' ? '#8b5cf6' : 'transparent',
                transition: 'all 0.3s ease',
                boxShadow: mode === 'review' ? '0 0 16px rgba(139, 92, 246, 0.8)' : 'none'
              }} />
            </div>
          </div>
        </div>

        {/* Animated Form Content Container */}
        <div style={{
          padding: '28px',
          overflowY: 'auto',
          flex: 1,
          position: 'relative'
        }}>
          <div style={{
            transform: isTransitioning ? 'translateX(-100px)' : 'translateX(0)',
            opacity: isTransitioning ? 0 : 1,
            transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
            filter: isTransitioning ? 'blur(4px)' : 'blur(0px)'
          }}>
            {mode === 'submit' ? (
              // Enhanced submit product form
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '28px',
                animation: 'slideInFromRight 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                
                {/* Enhanced Product Image Upload */}
                <div style={{
                  animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both'
                }}>
                  <label style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#e5e5e5',
                    marginBottom: '16px',
                    display: 'block',
                    background: 'linear-gradient(135deg, #e5e5e5 0%, #00d4aa 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Product Photo
                  </label>
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    style={{
                      border: '2px dashed #333',
                      borderRadius: '16px',
                      padding: '40px',
                      textAlign: 'center',
                      cursor: 'pointer',
                      backgroundColor: labelImagePreview ? 'transparent' : '#2a2a2a',
                      backgroundImage: labelImagePreview ? `url(${labelImagePreview})` : 'none',
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      minHeight: '220px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#00d4aa';
                      e.currentTarget.style.transform = 'scale(1.02)';
                      e.currentTarget.style.boxShadow = '0 16px 32px rgba(0, 212, 170, 0.15)';
                      if (!labelImagePreview) {
                        e.currentTarget.style.backgroundColor = '#333';
                      }
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#333';
                      e.currentTarget.style.transform = 'scale(1)';
                      e.currentTarget.style.boxShadow = 'none';
                      if (!labelImagePreview) {
                        e.currentTarget.style.backgroundColor = '#2a2a2a';
                      }
                    }}
                  >
                    {!labelImagePreview ? (
                      <div style={{ 
                        color: '#888',
                        animation: 'pulse 2s ease-in-out infinite'
                      }}>
                        <Camera size={64} style={{ 
                          marginBottom: '16px',
                          filter: 'drop-shadow(0 4px 8px rgba(0, 212, 170, 0.3))'
                        }} />
                        <p style={{ margin: 0, fontSize: '18px', fontWeight: '700', color: '#e5e5e5' }}>Click to upload product photo</p>
                        <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>Supports JPG, PNG (Max 5MB)</p>
                      </div>
                    ) : (
                      <div style={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: 'linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0.6) 100%)',
                        opacity: 0,
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        borderRadius: '14px',
                        backdropFilter: 'blur(4px)'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '0'}
                      >
                        <div style={{ color: 'white', textAlign: 'center' }}>
                          <Upload size={40} style={{ 
                            marginBottom: '12px',
                            filter: 'drop-shadow(0 4px 8px rgba(255, 255, 255, 0.3))'
                          }} />
                          <p style={{ margin: 0, fontSize: '16px', fontWeight: '700' }}>Click to change photo</p>
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    style={{ display: 'none' }}
                  />
                </div>

                {/* Enhanced Basic Product Info */}
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: '1fr 1fr', 
                  gap: '20px',
                  animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both'
                }}>
                  <div>
                    <label style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#e5e5e5',
                      marginBottom: '12px',
                      display: 'block'
                    }}>
                      Brand *
                    </label>
                    <input
                      type="text"
                      value={formData.brand}
                      onChange={(e) => setFormData(prev => ({ ...prev, brand: e.target.value }))}
                      placeholder="Enter brand name"
                      style={{
                        width: '100%',
                        padding: '16px',
                        backgroundColor: 'rgba(42, 42, 42, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        color: '#e5e5e5',
                        fontSize: '15px',
                        boxSizing: 'border-box',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        backdropFilter: 'blur(10px)'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#00d4aa';
                        e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 1)';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 212, 170, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 0.8)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    />
                  </div>
                  
                  <div>
                    <label style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: '#e5e5e5',
                      marginBottom: '12px',
                      display: 'block'
                    }}>
                      Product Name *
                    </label>
                    <input
                      type="text"
                      value={formData.product_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, product_name: e.target.value }))}
                      placeholder="Enter product name"
                      style={{
                        width: '100%',
                        padding: '16px',
                        backgroundColor: 'rgba(42, 42, 42, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        color: '#e5e5e5',
                        fontSize: '15px',
                        boxSizing: 'border-box',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        backdropFilter: 'blur(10px)'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#00d4aa';
                        e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 1)';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 212, 170, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 0.8)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    />
                  </div>
                </div>

                {/* Enhanced Product Type Selection */}
                <div style={{
                  animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.3s both'
                }}>
                  <label style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#e5e5e5',
                    marginBottom: '16px',
                    display: 'block',
                    background: 'linear-gradient(135deg, #e5e5e5 0%, #00d4aa 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Product Type *
                  </label>
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', 
                    gap: '12px' 
                  }}>
                    {productTypes.map((type, index) => (
                      <button
                        key={type.value}
                        onClick={() => setFormData(prev => ({ ...prev, type: type.value }))}
                        style={{
                          padding: '16px 12px',
                          backgroundColor: formData.type === type.value ? '#00d4aa' : 'rgba(42, 42, 42, 0.8)',
                          color: formData.type === type.value ? '#0a0a0a' : '#e5e5e5',
                          border: '1px solid',
                          borderColor: formData.type === type.value ? '#00d4aa' : 'rgba(255, 255, 255, 0.1)',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: '600',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '6px',
                          textAlign: 'center',
                          position: 'relative',
                          overflow: 'hidden',
                          backdropFilter: 'blur(10px)',
                          boxShadow: formData.type === type.value ? '0 8px 16px rgba(0, 212, 170, 0.3)' : 'none',
                          animation: `fadeInScale 0.6s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s both`
                        }}
                        onMouseEnter={(e) => {
                          if (formData.type !== type.value) {
                            e.currentTarget.style.backgroundColor = 'rgba(51, 51, 51, 0.9)';
                            e.currentTarget.style.borderColor = '#00d4aa';
                            e.currentTarget.style.transform = 'translateY(-4px) scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 12px 24px rgba(0, 212, 170, 0.2)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (formData.type !== type.value) {
                            e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 0.8)';
                            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                            e.currentTarget.style.transform = 'translateY(0) scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                          }
                        }}
                      >
                        {/* Selection indicator */}
                        {formData.type === type.value && (
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.1) 50%, transparent 70%)',
                            animation: 'shine 2s ease-in-out infinite',
                            pointerEvents: 'none'
                          }} />
                        )}
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Enhanced Product Attributes */}
                <div style={{
                  background: 'linear-gradient(135deg, rgba(42, 42, 42, 0.8) 0%, rgba(31, 31, 31, 0.9) 100%)',
                  borderRadius: '16px',
                  padding: '24px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
                  animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.4s both'
                }}>
                  <h3 style={{
                    margin: '0 0 20px 0',
                    fontSize: '20px',
                    fontWeight: '700',
                    color: '#e5e5e5',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '8px',
                      background: 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      animation: 'pulse 2s ease-in-out infinite'
                    }}>
                      <Package size={18} style={{ color: 'white' }} />
                    </div>
                    Product Details (Optional)
                  </h3>
                  
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', 
                    gap: '20px' 
                  }}>
                    <div>
                      <label style={{ 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#b3b3b3', 
                        marginBottom: '8px', 
                        display: 'block' 
                      }}>
                        THC %
                      </label>
                      <input
                        type="text"
                        value={formData.attributes.THC}
                        onChange={(e) => handleAttributeChange('THC', e.target.value)}
                        placeholder="e.g., 22%"
                        style={{
                          width: '100%',
                          padding: '12px',
                          backgroundColor: 'rgba(26, 26, 26, 0.8)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          color: '#e5e5e5',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          backdropFilter: 'blur(5px)'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#00d4aa';
                          e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 212, 170, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                    
                    <div>
                      <label style={{ 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#b3b3b3', 
                        marginBottom: '8px', 
                        display: 'block' 
                      }}>
                        CBD %
                      </label>
                      <input
                        type="text"
                        value={formData.attributes.CBD}
                        onChange={(e) => handleAttributeChange('CBD', e.target.value)}
                        placeholder="e.g., 0.5%"
                        style={{
                          width: '100%',
                          padding: '12px',
                          backgroundColor: 'rgba(26, 26, 26, 0.8)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          color: '#e5e5e5',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          backdropFilter: 'blur(5px)'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#00d4aa';
                          e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 212, 170, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div>
                      <label style={{ 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#b3b3b3', 
                        marginBottom: '8px', 
                        display: 'block' 
                      }}>
                        Strain Type
                      </label>
                      <select
                        value={formData.attributes.strain_type}
                        onChange={(e) => handleAttributeChange('strain_type', e.target.value)}
                        style={{
                          width: '100%',
                          padding: '12px',
                          backgroundColor: 'rgba(26, 26, 26, 0.8)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          color: '#e5e5e5',
                          fontSize: '14px',
                          cursor: 'pointer',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          backdropFilter: 'blur(5px)'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#00d4aa';
                          e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 212, 170, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      >
                        <option value="">Select type</option>
                        {strainTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label style={{ 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#b3b3b3', 
                        marginBottom: '8px', 
                        display: 'block' 
                      }}>
                        Size/Weight
                      </label>
                      <input
                        type="text"
                        value={formData.attributes.size}
                        onChange={(e) => handleAttributeChange('size', e.target.value)}
                        placeholder="e.g., 3.5g, 1oz"
                        style={{
                          width: '100%',
                          padding: '12px',
                          backgroundColor: 'rgba(26, 26, 26, 0.8)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          color: '#e5e5e5',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          backdropFilter: 'blur(5px)'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#00d4aa';
                          e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 212, 170, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                    </div>

                    <div style={{ gridColumn: '1 / -1' }}>
                      <label style={{ 
                        fontSize: '14px', 
                        fontWeight: '600', 
                        color: '#b3b3b3', 
                        marginBottom: '8px', 
                        display: 'block' 
                      }}>
                        Flavor Profile
                      </label>
                      <input
                        type="text"
                        value={formData.attributes.flavor}
                        onChange={(e) => handleAttributeChange('flavor', e.target.value)}
                        placeholder="e.g., Sweet, Citrus, Earthy"
                        style={{
                          width: '100%',
                          padding: '12px',
                          backgroundColor: 'rgba(26, 26, 26, 0.8)',
                          border: '1px solid rgba(255, 255, 255, 0.1)',
                          borderRadius: '8px',
                          color: '#e5e5e5',
                          fontSize: '14px',
                          boxSizing: 'border-box',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          backdropFilter: 'blur(5px)'
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.borderColor = '#00d4aa';
                          e.currentTarget.style.boxShadow = '0 0 0 2px rgba(0, 212, 170, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                          e.currentTarget.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                  </div>

                  {/* Enhanced Terpenes */}
                  <div style={{ marginTop: '20px' }}>
                    <label style={{ 
                      fontSize: '14px', 
                      fontWeight: '600', 
                      color: '#b3b3b3', 
                      marginBottom: '12px', 
                      display: 'block' 
                    }}>
                      Terpenes
                    </label>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {commonTerpenes.map((terpene, index) => (
                        <button
                          key={terpene}
                          onClick={() => handleTerpeneToggle(terpene)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: formData.attributes.terpenes.includes(terpene) ? '#8b5cf6' : 'rgba(51, 51, 51, 0.8)',
                            color: 'white',
                            border: '1px solid',
                            borderColor: formData.attributes.terpenes.includes(terpene) ? '#8b5cf6' : 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '20px',
                            fontSize: '12px',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            fontWeight: '600',
                            backdropFilter: 'blur(5px)',
                            animation: `fadeInScale 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.05}s both`,
                            boxShadow: formData.attributes.terpenes.includes(terpene) ? '0 4px 12px rgba(139, 92, 246, 0.3)' : 'none'
                          }}
                          onMouseEnter={(e) => {
                            if (!formData.attributes.terpenes.includes(terpene)) {
                              e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.3)';
                              e.currentTarget.style.borderColor = '#8b5cf6';
                              e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                              e.currentTarget.style.boxShadow = '0 8px 16px rgba(139, 92, 246, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!formData.attributes.terpenes.includes(terpene)) {
                              e.currentTarget.style.backgroundColor = 'rgba(51, 51, 51, 0.8)';
                              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                              e.currentTarget.style.transform = 'translateY(0) scale(1)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                        >
                          {terpene}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Enhanced Rating */}
                <div style={{
                  animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.5s both'
                }}>
                  <label style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#e5e5e5',
                    marginBottom: '16px',
                    display: 'block',
                    background: 'linear-gradient(135deg, #e5e5e5 0%, #fbbf24 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Your Rating
                  </label>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        onClick={() => setFormData(prev => ({ ...prev, rating: star }))}
                        style={{
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: '8px',
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          borderRadius: '50%'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.2) rotate(15deg)';
                          e.currentTarget.style.backgroundColor = 'rgba(251, 191, 36, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        <Star
                          size={36}
                          style={{
                            color: star <= formData.rating ? '#fbbf24' : '#444',
                            fill: star <= formData.rating ? '#fbbf24' : 'none',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            filter: star <= formData.rating ? 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.6))' : 'none'
                          }}
                        />
                      </button>
                    ))}
                    {formData.rating > 0 && (
                      <div style={{
                        marginLeft: '16px',
                        padding: '8px 16px',
                        background: 'linear-gradient(135deg, rgba(0, 212, 170, 0.2) 0%, rgba(0, 212, 170, 0.1) 100%)',
                        borderRadius: '12px',
                        border: '1px solid rgba(0, 212, 170, 0.3)',
                        animation: 'fadeInScale 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                      }}>
                        <span style={{ 
                          color: '#00d4aa', 
                          fontSize: '16px', 
                          fontWeight: '700',
                          textShadow: '0 0 10px rgba(0, 212, 170, 0.5)'
                        }}>
                          {formData.rating}/5 stars
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Enhanced Review */}
                <div style={{
                  animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.6s both'
                }}>
                  <label style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#e5e5e5',
                    marginBottom: '12px',
                    display: 'block',
                    background: 'linear-gradient(135deg, #e5e5e5 0%, #00d4aa 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Your Review
                  </label>
                  <textarea
                    value={formData.review}
                    onChange={(e) => setFormData(prev => ({ ...prev, review: e.target.value }))}
                    placeholder="Share your experience with this product... What did you like? How did it make you feel? Would you recommend it?"
                    rows={4}
                    style={{
                      width: '100%',
                      padding: '16px',
                      backgroundColor: 'rgba(42, 42, 42, 0.8)',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '12px',
                      color: '#e5e5e5',
                      fontSize: '15px',
                      resize: 'vertical',
                      boxSizing: 'border-box',
                      fontFamily: 'inherit',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      backdropFilter: 'blur(10px)'
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#00d4aa';
                      e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 1)';
                      e.currentTarget.style.boxShadow = '0 0 0 3px rgba(0, 212, 170, 0.1)';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 0.8)';
                      e.currentTarget.style.boxShadow = 'none';
                      e.currentTarget.style.transform = 'translateY(0)';
                    }}
                  />
                  <div style={{
                    fontSize: '12px',
                    color: '#888',
                    marginTop: '8px',
                    textAlign: 'right',
                    opacity: 0.8
                  }}>
                    {formData.review.length}/500 characters
                  </div>
                </div>

                {isAuthenticated ? (
                  // Show checkbox for logged-in users
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    background: 'rgba(139, 92, 246, 0.1)',
                    borderRadius: '8px',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.6s both'
                  }}>
                    <input
                      type="checkbox"
                      id="anonymous"
                      checked={isAnonymous}
                      onChange={(e) => setIsAnonymous(e.target.checked)}
                      style={{
                        accentColor: '#8b5cf6',
                        transform: 'scale(1.2)',
                        cursor: 'pointer'
                      }}
                    />
                    <label 
                      htmlFor="anonymous"
                      style={{
                        color: '#e5e5e5',
                        fontSize: '14px',
                        cursor: 'pointer',
                        userSelect: 'none'
                      }}
                    >
                      Submit anonymously (your username won't be shown)
                    </label>
                  </div>
                ) : (
                  // Show info for non-logged-in users
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '12px',
                    background: 'rgba(245, 158, 11, 0.1)',
                    borderRadius: '8px',
                    border: '1px solid rgba(245, 158, 11, 0.3)',
                    animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.6s both'
                  }}>
                    <span style={{ fontSize: '20px' }}>ℹ️</span>
                    <span style={{
                      color: '#f59e0b',
                      fontSize: '13px',
                      lineHeight: '1.4'
                    }}>
                      You're not logged in. This submission will be posted anonymously. 
                      <a href="/login" style={{ color: '#00d4aa', marginLeft: '4px' }}>Log in</a> to get credit for your contribution!
                    </span>
                  </div>
                )}

                {/* Enhanced Submit Button */}
                <div style={{ 
                  display: 'flex', 
                  gap: '16px',
                  animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.7s both'
                }}>
                  <button
                    onClick={onClose}
                    style={{
                      padding: '18px 28px',
                      background: 'rgba(255, 255, 255, 0.05)',
                      color: '#888',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      borderRadius: '16px',
                      fontSize: '16px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      flex: '0 0 auto',
                      backdropFilter: 'blur(10px)'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                      e.currentTarget.style.color = '#fff';
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.color = '#888';
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    Cancel
                  </button>
                  
                  <button
                    onClick={handleSubmit}
                    disabled={isSubmitting || !formData.brand || !formData.product_name}
                    style={{
                      padding: '18px 36px',
                      background: isSubmitting || (!formData.brand || !formData.product_name) 
                        ? 'rgba(51, 51, 51, 0.8)' 
                        : 'linear-gradient(135deg, #00d4aa 0%, #10b981 50%, #059669 100%)',
                      color: isSubmitting || (!formData.brand || !formData.product_name) ? '#888' : '#0a0a0a',
                      border: 'none',
                      borderRadius: '16px',
                      fontSize: '16px',
                      fontWeight: '700',
                      cursor: isSubmitting || (!formData.brand || !formData.product_name) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '12px',
                      flex: 1,
                      boxShadow: isSubmitting || (!formData.brand || !formData.product_name) 
                        ? 'none' 
                        : '0 8px 20px rgba(0, 212, 170, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubmitting && formData.brand && formData.product_name) {
                        e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                        e.currentTarget.style.boxShadow = '0 16px 32px rgba(0, 212, 170, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubmitting && formData.brand && formData.product_name) {
                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                        e.currentTarget.style.boxShadow = '0 8px 20px rgba(0, 212, 170, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                      }
                    }}
                  >
                    {/* Button shine effect */}
                    {!isSubmitting && formData.brand && formData.product_name && (
                      <div style={{
                        position: 'absolute',
                        top: '-50%',
                        left: '-50%',
                        width: '200%',
                        height: '200%',
                        background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
                        animation: 'shine 3s ease-in-out infinite',
                        pointerEvents: 'none'
                      }} />
                    )}
                    
                    {isSubmitting ? (
                      <>
                        <div style={{
                          width: '20px',
                          height: '20px',
                          border: '3px solid rgba(136, 136, 136, 0.3)',
                          borderTop: '3px solid #888',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Award size={20} />
                        Submit Product & Review
                      </>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              // Enhanced review existing product form
              <div style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                gap: '28px',
                animation: 'slideInFromLeft 0.6s cubic-bezier(0.4, 0, 0.2, 1)'
              }}>
                
                {/* Enhanced Product Search */}
                <div style={{
                  animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.1s both'
                }}>
                  <label style={{
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#e5e5e5',
                    marginBottom: '16px',
                    display: 'block',
                    background: 'linear-gradient(135deg, #e5e5e5 0%, #8b5cf6 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text'
                  }}>
                    Search for Product
                  </label>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search by product name or brand..."
                      style={{
                        flex: 1,
                        padding: '16px',
                        backgroundColor: 'rgba(42, 42, 42, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        color: '#e5e5e5',
                        fontSize: '15px',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        backdropFilter: 'blur(10px)'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#8b5cf6';
                        e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 1)';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 0.8)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                    />
                    <button
                      onClick={handleSearch}
                      disabled={!searchQuery.trim() || isSearching}
                      style={{
                        padding: '16px 24px',
                        background: !searchQuery.trim() || isSearching 
                          ? 'rgba(51, 51, 51, 0.8)' 
                          : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)',
                        color: !searchQuery.trim() || isSearching ? '#888' : 'white',
                        border: 'none',
                        borderRadius: '12px',
                        fontSize: '15px',
                        fontWeight: '700',
                        cursor: !searchQuery.trim() || isSearching ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        boxShadow: !searchQuery.trim() || isSearching 
                          ? 'none' 
                          : '0 8px 16px rgba(139, 92, 246, 0.3)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        if (!(!searchQuery.trim() || isSearching)) {
                          e.currentTarget.style.transform = 'translateY(-2px) scale(1.05)';
                          e.currentTarget.style.boxShadow = '0 12px 24px rgba(139, 92, 246, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!(!searchQuery.trim() || isSearching)) {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          e.currentTarget.style.boxShadow = '0 8px 16px rgba(139, 92, 246, 0.3)';
                        }
                      }}
                    >
                      {/* Button shine effect */}
                      {!(!searchQuery.trim() || isSearching) && (
                        <div style={{
                          position: 'absolute',
                          top: '-50%',
                          left: '-50%',
                          width: '200%',
                          height: '200%',
                          background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
                          animation: 'shine 3s ease-in-out infinite',
                          pointerEvents: 'none'
                        }} />
                      )}
                      
                      {isSearching ? (
                        <div style={{
                          width: '18px',
                          height: '18px',
                          border: '3px solid rgba(136, 136, 136, 0.3)',
                          borderTop: '3px solid #888',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }} />
                      ) : (
                        <Search size={18} />
                      )}
                      Search
                    </button>
                  </div>
                </div>

                {/* Enhanced Search Results */}
                {searchResults.length > 0 && (
                  <div style={{
                    animation: 'fadeInUp 0.6s cubic-bezier(0.4, 0, 0.2, 1) 0.2s both'
                  }}>
                    <label style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#e5e5e5',
                      marginBottom: '16px',
                      display: 'block',
                      background: 'linear-gradient(135deg, #e5e5e5 0%, #8b5cf6 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      Select Product
                    </label>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '12px', 
                      maxHeight: '300px', 
                      overflowY: 'auto',
                      paddingRight: '8px'
                    }}>
                      {searchResults.map((product, index) => (
                        <button
                          key={product.id}
                          onClick={() => handleProductSelect(product)}
                          style={{
                            padding: '20px',
                            backgroundColor: selectedProduct?.id === product.id ? 'rgba(139, 92, 246, 0.2)' : 'rgba(42, 42, 42, 0.8)',
                            border: '1px solid',
                            borderColor: selectedProduct?.id === product.id ? '#8b5cf6' : 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '12px',
                            color: '#e5e5e5',
                            cursor: 'pointer',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            textAlign: 'left',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            backdropFilter: 'blur(10px)',
                            boxShadow: selectedProduct?.id === product.id ? '0 8px 16px rgba(139, 92, 246, 0.3)' : 'none',
                            animation: `fadeInScale 0.5s cubic-bezier(0.4, 0, 0.2, 1) ${index * 0.1}s both`
                          }}
                          onMouseEnter={(e) => {
                            if (selectedProduct?.id !== product.id) {
                              e.currentTarget.style.backgroundColor = 'rgba(139, 92, 246, 0.1)';
                              e.currentTarget.style.borderColor = '#8b5cf6';
                              e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                              e.currentTarget.style.boxShadow = '0 12px 24px rgba(139, 92, 246, 0.2)';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (selectedProduct?.id !== product.id) {
                              e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 0.8)';
                              e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                              e.currentTarget.style.transform = 'translateY(0) scale(1)';
                              e.currentTarget.style.boxShadow = 'none';
                            }
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            {/* Product Name and Brand */}
                            <div style={{ 
                              fontSize: '17px', 
                              fontWeight: '700', 
                              marginBottom: '8px',
                              color: selectedProduct?.id === product.id ? '#8b5cf6' : '#e5e5e5'
                            }}>
                              {product.brand} - {product.product_name}
                            </div>
                            
                            {/* Product Type and Rating */}
                            <div style={{ 
                              fontSize: '14px', 
                              color: '#888', 
                              display: 'flex', 
                              alignItems: 'center', 
                              gap: '16px',
                              marginBottom: '12px'
                            }}>
                              <span>{productTypes.find(t => t.value === product.type)?.label || product.type}</span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                                <Star size={14} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
                                {product.rating} ({product.reviewCount} reviews)
                              </span>
                            </div>

                            {/* Product Attributes */}
                            {product.attributes && Object.keys(product.attributes).length > 0 && (
                              <div style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '8px',
                                marginTop: '8px'
                              }}>
                                {/* Parse attributes if it's a JSON string, otherwise use as array */}
                                {(() => {
                                  let attrs;
                                  try {
                                    attrs = typeof product.attributes === 'string' 
                                      ? JSON.parse(product.attributes) 
                                      : product.attributes;
                                  } catch (e) {
                                    attrs = product.attributes;
                                  }

                                  const attributesToShow = [];
                                  
                                  // Add THC if present
                                  if (attrs.THC) {
                                    attributesToShow.push({
                                      label: 'THC',
                                      value: attrs.THC,
                                      color: '#10b981'
                                    });
                                  }
                                  
                                  // Add CBD if present
                                  if (attrs.CBD) {
                                    attributesToShow.push({
                                      label: 'CBD',
                                      value: attrs.CBD,
                                      color: '#8b5cf6'
                                    });
                                  }
                                  
                                  // Add strain type if present
                                  if (attrs.strain_type) {
                                    attributesToShow.push({
                                      label: 'Strain',
                                      value: attrs.strain_type.charAt(0).toUpperCase() + attrs.strain_type.slice(1),
                                      color: '#f59e0b'
                                    });
                                  }
                                  
                                  // Add size if present
                                  if (attrs.size) {
                                    attributesToShow.push({
                                      label: 'Size',
                                      value: attrs.size,
                                      color: '#6366f1'
                                    });
                                  }

                                  return attributesToShow.map((attr, idx) => (
                                    <div
                                      key={idx}
                                      style={{
                                        padding: '4px 8px',
                                        backgroundColor: `${attr.color}20`,
                                        border: `1px solid ${attr.color}40`,
                                        borderRadius: '8px',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        color: attr.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '4px'
                                      }}
                                    >
                                      <span style={{ opacity: 0.8 }}>{attr.label}:</span>
                                      <span>{attr.value}</span>
                                    </div>
                                  ));
                                })()}
                              </div>
                            )}
                          </div>
                          
                          {/* Selection Indicator */}
                          {selectedProduct?.id === product.id && (
                            <div style={{
                              width: '24px',
                              height: '24px',
                              borderRadius: '50%',
                              background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              animation: 'pulse 2s ease-in-out infinite',
                              boxShadow: '0 0 16px rgba(139, 92, 246, 0.6)',
                              flexShrink: 0,
                              marginLeft: '16px'
                            }}>
                              <div style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: 'white'
                              }} />
                            </div>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Enhanced Selected Product Info */}
                {selectedProduct && (
                  <div style={{
                    background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.15) 0%, rgba(124, 58, 237, 0.1) 100%)',
                    borderRadius: '16px',
                    padding: '24px',
                    border: '1px solid rgba(139, 92, 246, 0.3)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 8px 16px rgba(139, 92, 246, 0.2)',
                    animation: 'fadeInScale 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Animated background orb */}
                    <div style={{
                      position: 'absolute',
                      top: '-30%',
                      right: '-20%',
                      width: '120px',
                      height: '120px',
                      background: 'radial-gradient(circle, rgba(139, 92, 246, 0.2) 0%, transparent 70%)',
                      borderRadius: '50%',
                      animation: 'float 4s ease-in-out infinite',
                      pointerEvents: 'none'
                    }} />
                    
                    <h3 style={{
                      margin: '0 0 16px 0',
                      fontSize: '20px',
                      fontWeight: '700',
                      color: '#e5e5e5',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '8px',
                        background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        animation: 'glow 2s ease-in-out infinite alternate'
                      }}>
                        <Package size={18} style={{ color: 'white' }} />
                      </div>
                      Selected Product
                    </h3>
                    <div style={{
                      fontSize: '18px',
                      color: '#8b5cf6',
                      fontWeight: '700',
                      marginBottom: '12px',
                      textShadow: '0 0 10px rgba(139, 92, 246, 0.5)',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      {selectedProduct.brand} - {selectedProduct.product_name}
                    </div>
                    <div style={{
                      fontSize: '15px',
                      color: '#b3b3b3',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                      position: 'relative',
                      zIndex: 1
                    }}>
                      <span>{productTypes.find(t => t.value === selectedProduct.type)?.label || selectedProduct.type}</span>
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Star size={16} style={{ color: '#fbbf24', fill: '#fbbf24' }} />
                        {selectedProduct.rating} ({selectedProduct.reviewCount} reviews)
                      </span>
                    </div>
                  </div>
                )}

                {/* Enhanced Review Rating */}
                {selectedProduct && (
                  <div style={{
                    animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.3s both'
                  }}>
                    <label style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#e5e5e5',
                      marginBottom: '16px',
                      display: 'block',
                      background: 'linear-gradient(135deg, #e5e5e5 0%, #fbbf24 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      Your Rating
                    </label>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          onClick={() => setReviewData(prev => ({ ...prev, rating: star }))}
                          style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: '8px',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            borderRadius: '50%'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.2) rotate(15deg)';
                            e.currentTarget.style.backgroundColor = 'rgba(251, 191, 36, 0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          <Star
                            size={36}
                            style={{
                              color: star <= reviewData.rating ? '#fbbf24' : '#444',
                              fill: star <= reviewData.rating ? '#fbbf24' : 'none',
                              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                              filter: star <= reviewData.rating ? 'drop-shadow(0 0 8px rgba(251, 191, 36, 0.6))' : 'none'
                            }}
                          />
                        </button>
                      ))}
                      {reviewData.rating > 0 && (
                        <div style={{
                          marginLeft: '16px',
                          padding: '8px 16px',
                          background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.2) 0%, rgba(139, 92, 246, 0.1) 100%)',
                          borderRadius: '12px',
                          border: '1px solid rgba(139, 92, 246, 0.3)',
                          animation: 'fadeInScale 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                        }}>
                          <span style={{ 
                            color: '#8b5cf6', 
                            fontSize: '16px', 
                            fontWeight: '700',
                            textShadow: '0 0 10px rgba(139, 92, 246, 0.5)'
                          }}>
                            {reviewData.rating}/5 stars
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {selectedProduct && (
                  <div style={{
                    animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.4s both'
                  }}>
                    <label style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: '#e5e5e5',
                      marginBottom: '12px',
                      display: 'block',
                      background: 'linear-gradient(135deg, #e5e5e5 0%, #8b5cf6 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      backgroundClip: 'text'
                    }}>
                      Your Review *
                    </label>
                    <textarea
                      value={reviewData.content}
                      onChange={(e) => setReviewData(prev => ({ ...prev, content: e.target.value }))}
                      placeholder="Share your experience with this product... What did you like? How did it make you feel? Would you recommend it?"
                      rows={4}
                      style={{
                        width: '100%',
                        padding: '16px',
                        backgroundColor: 'rgba(42, 42, 42, 0.8)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '12px',
                        color: '#e5e5e5',
                        fontSize: '15px',
                        resize: 'vertical',
                        boxSizing: 'border-box',
                        fontFamily: 'inherit',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        backdropFilter: 'blur(10px)'
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#8b5cf6';
                        e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 1)';
                        e.currentTarget.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.backgroundColor = 'rgba(42, 42, 42, 0.8)';
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.transform = 'translateY(0)';
                      }}
                    />
                    <div style={{
                      fontSize: '12px',
                      color: '#888',
                      marginTop: '8px',
                      textAlign: 'right',
                      opacity: 0.8
                    }}>
                      {reviewData.content.length}/1000 characters
                    </div>
                  </div>
                )}

                {/* Enhanced Review Content */}
                {selectedProduct && (
                <>
                  {/* Add Anonymous Checkbox Here - Before the buttons */}
                  <div style={{
                    animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.45s both'
                  }}>
                    {isAuthenticated ? (
                      // Show checkbox for logged-in users
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px',
                        background: 'rgba(139, 92, 246, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(139, 92, 246, 0.3)',
                        marginBottom: '16px'
                      }}>
                        <input
                          type="checkbox"
                          id="anonymous-review"
                          checked={isAnonymous}
                          onChange={(e) => setIsAnonymous(e.target.checked)}
                          style={{
                            accentColor: '#8b5cf6',
                            transform: 'scale(1.2)',
                            cursor: 'pointer'
                          }}
                        />
                        <label 
                          htmlFor="anonymous-review"
                          style={{
                            color: '#e5e5e5',
                            fontSize: '14px',
                            cursor: 'pointer',
                            userSelect: 'none'
                          }}
                        >
                          Submit anonymously (your username won't be shown)
                        </label>
                      </div>
                    ) : (
                      // Show info for non-logged-in users
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '12px',
                        background: 'rgba(245, 158, 11, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        marginBottom: '16px'
                      }}>
                        <span style={{ fontSize: '20px' }}>ℹ️</span>
                        <span style={{
                          color: '#f59e0b',
                          fontSize: '13px',
                          lineHeight: '1.4'
                        }}>
                          You're not logged in. This review will be posted anonymously. 
                          <a href="/login" style={{ color: '#00d4aa', marginLeft: '4px', textDecoration: 'none' }}>Log in</a> to get credit for your contribution!
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Existing buttons */}
                  <div style={{ 
                    display: 'flex', 
                    gap: '16px',
                    animation: 'fadeInUp 0.8s cubic-bezier(0.4, 0, 0.2, 1) 0.5s both'
                  }}>
                    <button
                      onClick={onClose}
                      style={{
                        padding: '18px 28px',
                        background: 'rgba(255, 255, 255, 0.05)',
                        color: '#888',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                        borderRadius: '16px',
                        fontSize: '16px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        flex: '0 0 auto',
                        backdropFilter: 'blur(10px)'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                        e.currentTarget.style.color = '#fff';
                        e.currentTarget.style.transform = 'translateY(-2px)';
                        e.currentTarget.style.boxShadow = '0 8px 16px rgba(0, 0, 0, 0.2)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                        e.currentTarget.style.color = '#888';
                        e.currentTarget.style.transform = 'translateY(0)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      Cancel
                    </button>
                    
                    <button onClick={handleSubmitReview}
                      disabled={isSubmitting || !reviewData.rating || !reviewData.content}
                      style={{
                        padding: '18px 36px',
                        background: isSubmitting || (!reviewData.rating || !reviewData.content) 
                          ? 'rgba(51, 51, 51, 0.8)' 
                          : 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 50%, #6d28d9 100%)',
                        color: isSubmitting || (!reviewData.rating || !reviewData.content) ? '#888' : 'white',
                        border: 'none',
                        borderRadius: '16px',
                        fontSize: '16px',
                        fontWeight: '700',
                        cursor: isSubmitting || (!reviewData.rating || !reviewData.content) ? 'not-allowed' : 'pointer',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '12px',
                        flex: 1,
                        boxShadow: isSubmitting || (!reviewData.rating || !reviewData.content) 
                          ? 'none' 
                          : '0 8px 20px rgba(139, 92, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)',
                        position: 'relative',
                        overflow: 'hidden'
                      }}
                      onMouseEnter={(e) => {
                        if (!isSubmitting && reviewData.rating && reviewData.content) {
                          e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                          e.currentTarget.style.boxShadow = '0 16px 32px rgba(139, 92, 246, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.3)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isSubmitting && reviewData.rating && reviewData.content) {
                          e.currentTarget.style.transform = 'translateY(0) scale(1)';
                          e.currentTarget.style.boxShadow = '0 8px 20px rgba(139, 92, 246, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.2)';
                        }
                      }}
                    >
                      {/* Button shine effect */}
                      {!isSubmitting && reviewData.rating && reviewData.content && (
                        <div style={{
                          position: 'absolute',
                          top: '-50%',
                          left: '-50%',
                          width: '200%',
                          height: '200%',
                          background: 'linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.2) 50%, transparent 70%)',
                          animation: 'shine 3s ease-in-out infinite',
                          pointerEvents: 'none'
                        }} />
                      )}
                      
                      {isSubmitting ? (
                        <>
                          <div style={{
                            width: '20px',
                            height: '20px',
                            border: '3px solid rgba(136, 136, 136, 0.3)',
                            borderTop: '3px solid #888',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                          }} />
                          Submitting...
                        </>
                      ) : (
                        <>
                          <Star size={20} />
                          Submit Review
                        </>
                      )}
                    </button>
                  </div>
                </>
              )}

                {/* Enhanced No results message */}
                {searchResults.length === 0 && searchQuery && !isSearching && (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#888',
                    background: 'linear-gradient(135deg, rgba(42, 42, 42, 0.8) 0%, rgba(31, 31, 31, 0.9) 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(10px)',
                    animation: 'fadeInScale 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {/* Animated background orb */}
                    <div style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '200px',
                      height: '200px',
                      background: 'radial-gradient(circle, rgba(139, 92, 246, 0.1) 0%, transparent 70%)',
                      borderRadius: '50%',
                      animation: 'pulse 3s ease-in-out infinite',
                      pointerEvents: 'none'
                    }} />
                    
                    <div style={{ position: 'relative', zIndex: 1 }}>
                      <Search size={64} style={{ 
                        marginBottom: '16px', 
                        opacity: 0.5,
                        animation: 'float 3s ease-in-out infinite'
                      }} />
                      <p style={{ 
                        margin: 0, 
                        fontSize: '18px', 
                        fontWeight: '700',
                        color: '#e5e5e5',
                        marginBottom: '8px'
                      }}>No products found</p>
                      <p style={{ 
                        margin: 0, 
                        fontSize: '14px',
                        opacity: 0.8
                      }}>Try a different search term or submit a new product instead</p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes fadeInUp {
          0% { 
            opacity: 0; 
            transform: translateY(30px); 
          }
          100% { 
            opacity: 1; 
            transform: translateY(0); 
          }
        }
        
        @keyframes fadeInScale {
          0% { 
            opacity: 0; 
            transform: scale(0.9); 
          }
          100% { 
            opacity: 1; 
            transform: scale(1); 
          }
        }
        
        @keyframes slideInFromRight {
          0% { 
            opacity: 0; 
            transform: translateX(100px); 
          }
          100% { 
            opacity: 1; 
            transform: translateX(0); 
          }
        }
        
        @keyframes slideInFromLeft {
          0% { 
            opacity: 0; 
            transform: translateX(-100px); 
          }
          100% { 
            opacity: 1; 
            transform: translateX(0); 
          }
        }
        
        @keyframes float {
          0%, 100% { 
            transform: translateY(0px) rotate(0deg); 
          }
          50% { 
            transform: translateY(-20px) rotate(5deg); 
          }
        }
        
        @keyframes pulse {
          0%, 100% { 
            opacity: 1; 
            transform: scale(1); 
          }
          50% { 
            opacity: 0.8; 
            transform: scale(1.05); 
          }
        }
        
        @keyframes glow {
          0% { 
            box-shadow: 0 0 5px rgba(0, 212, 170, 0.5); 
          }
          100% { 
            box-shadow: 0 0 20px rgba(0, 212, 170, 0.8), 0 0 30px rgba(0, 212, 170, 0.3); 
          }
        }
        
        @keyframes shine {
          0% { 
            transform: translateX(-100%) translateY(-100%) rotate(45deg); 
          }
          100% { 
            transform: translateX(100%) translateY(100%) rotate(45deg); 
          }
        }
        
        /* Custom scrollbar for better aesthetics */
        ::-webkit-scrollbar {
          width: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(255, 255, 255, 0.05);
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #00d4aa 0%, #8b5cf6 100%);
          border-radius: 4px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #10b981 0%, #7c3aed 100%);
        }
      `}</style>
    </div>
  );
};